import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Paper, IconButton, Snackbar, Alert, Switch, FormControlLabel, Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import StarIcon from '@mui/icons-material/Star';
import { adminFetch } from '../../lib/adminAuth';

const DEFAULTS = {
  sectionTitle: 'Buy Credits',
  sectionSubtitle: 'Choose a package that fits your needs',
  balanceLabel: 'Your Credit Balance',
  balanceChip: 'Credits are used for sending notifications and promotions',
  packages: [
    { credits: 100, price: 25, label: 'Starter', highlighted: false, badge: '', features: ['100 notification credits', 'Standard support'] },
    { credits: 220, price: 50, label: 'Basic', highlighted: false, badge: '', features: ['220 notification credits', 'Email support'] },
    { credits: 480, price: 100, label: 'Growth', highlighted: true, badge: 'Most Popular', features: ['480 notification credits', 'Priority delivery', 'Priority support'] },
    { credits: 2500, price: 400, label: 'Scale', highlighted: false, badge: 'Best Value', features: ['2,500 notification credits', 'Best price per credit', 'Dedicated support'] },
  ],
};

export default function AdminCredits() {
  const [content, setContent] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    (async () => {
      try {
        const res = await adminFetch('/api/admin/credits-content');
        const data = await res.json();
        if (data && Object.keys(data).length > 1) {
          setContent({
            sectionTitle: data.sectionTitle || DEFAULTS.sectionTitle,
            sectionSubtitle: data.sectionSubtitle || DEFAULTS.sectionSubtitle,
            balanceLabel: data.balanceLabel || DEFAULTS.balanceLabel,
            balanceChip: data.balanceChip || DEFAULTS.balanceChip,
            packages: data.packages && data.packages.length > 0 ? data.packages : DEFAULTS.packages,
          });
        }
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  const updateField = (field, value) => setContent(prev => ({ ...prev, [field]: value }));

  const updatePackage = (index, field, value) => {
    setContent(prev => {
      const packages = [...prev.packages];
      packages[index] = { ...packages[index], [field]: value };
      return { ...prev, packages };
    });
  };

  const updateFeature = (pkgIndex, featIndex, value) => {
    setContent(prev => {
      const packages = [...prev.packages];
      const features = [...packages[pkgIndex].features];
      features[featIndex] = value;
      packages[pkgIndex] = { ...packages[pkgIndex], features };
      return { ...prev, packages };
    });
  };

  const addFeature = (pkgIndex) => {
    setContent(prev => {
      const packages = [...prev.packages];
      packages[pkgIndex] = { ...packages[pkgIndex], features: [...packages[pkgIndex].features, ''] };
      return { ...prev, packages };
    });
  };

  const removeFeature = (pkgIndex, featIndex) => {
    setContent(prev => {
      const packages = [...prev.packages];
      packages[pkgIndex] = { ...packages[pkgIndex], features: packages[pkgIndex].features.filter((_, i) => i !== featIndex) };
      return { ...prev, packages };
    });
  };

  // Mirrors the limits enforced by PUT /api/admin/credits-content.
  const MIN_PACKAGES = 1;
  const MAX_PACKAGES = 5;
  // Stripe won't process a charge under $0.50 AUD.
  const MIN_PACKAGE_PRICE = 0.5;
  const atMax = content.packages.length >= MAX_PACKAGES;
  const atMin = content.packages.length <= MIN_PACKAGES;

  const addPackage = () => {
    if (atMax) return;
    setContent(prev => ({
      ...prev,
      packages: [...prev.packages, { credits: 0, price: 0, label: 'New Package', highlighted: false, badge: '', features: [] }],
    }));
  };

  const removePackage = (index) => {
    if (atMin) {
      setSnack({ open: true, message: 'You need at least one credit package.', severity: 'warning' });
      return;
    }
    setContent(prev => ({ ...prev, packages: prev.packages.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminFetch('/api/admin/credits-content', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(content),
      });
      if (res.ok) {
        setSnack({ open: true, message: 'Credits content saved!', severity: 'success' });
      } else {
        const data = await res.json().catch(() => ({}));
        setSnack({ open: true, message: data.message || 'Failed to save', severity: 'error' });
      }
    } catch {
      setSnack({ open: true, message: 'Failed to save', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setContent(DEFAULTS);
    setSnack({ open: true, message: 'Reset to defaults (save to apply)', severity: 'info' });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e' }}>Credits & Pricing</Typography>
          <Typography variant="body2" sx={{ color: '#888' }}>Manage credit packages and pricing displayed to vendors</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button startIcon={<RestoreIcon />} onClick={handleReset} sx={{ textTransform: 'none', borderRadius: 2, color: '#888' }}>Reset</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
            sx={{ textTransform: 'none', borderRadius: 2, bgcolor: '#1a1a2e', color: '#fff', '&:hover': { bgcolor: '#16213e' } }}>
            {saving ? 'Saving...' : 'Save All'}
          </Button>
        </Box>
      </Box>

      {/* Section Text */}
      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Page Text</Typography>
        <TextField label="Section Title" fullWidth value={content.sectionTitle} onChange={e => updateField('sectionTitle', e.target.value)} sx={{ mb: 2 }} size="small" />
        <TextField label="Section Subtitle" fullWidth value={content.sectionSubtitle} onChange={e => updateField('sectionSubtitle', e.target.value)} sx={{ mb: 2 }} size="small" />
        <TextField label="Balance Label" fullWidth value={content.balanceLabel} onChange={e => updateField('balanceLabel', e.target.value)} sx={{ mb: 2 }} size="small" />
        <TextField label="Balance Chip Text" fullWidth value={content.balanceChip} onChange={e => updateField('balanceChip', e.target.value)} size="small" />
      </Paper>

      {/* Packages */}
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MonetizationOnIcon sx={{ color: '#ffb71b' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Credit Packages</Typography>
            <Typography variant="caption" sx={{ color: '#999', ml: 1 }}>
              ({content.packages.length} of {MAX_PACKAGES})
            </Typography>
          </Box>
          <Button
            startIcon={<AddIcon />}
            onClick={addPackage}
            size="small"
            disabled={atMax}
            title={atMax ? `Maximum of ${MAX_PACKAGES} packages` : undefined}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Add Package
          </Button>
        </Box>

        {content.packages.map((pkg, index) => (
          <Box key={index} sx={{ mb: 2, p: 2.5, border: '1px solid #e8e8e8', borderRadius: 2, bgcolor: pkg.highlighted ? '#fffdf5' : '#fafafa' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#999', fontWeight: 600 }}>Package #{index + 1}</Typography>
                {pkg.highlighted && <Chip label="Highlighted" size="small" sx={{ bgcolor: '#ffb71b', color: '#000', fontWeight: 600, fontSize: 10 }} />}
              </Box>
              <IconButton
                size="small"
                onClick={() => removePackage(index)}
                disabled={atMin}
                title={atMin ? 'At least one package is required' : 'Delete package'}
                sx={{ color: '#ccc', '&:hover': { color: '#e53935' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, mb: 1.5 }}>
              <TextField label="Label" value={pkg.label} onChange={e => updatePackage(index, 'label', e.target.value)} size="small" />
              <TextField label="Credits" type="number" value={pkg.credits} onChange={e => updatePackage(index, 'credits', Number(e.target.value))} size="small" />
              <TextField
                label="Price ($)"
                type="number"
                value={pkg.price}
                onChange={e => updatePackage(index, 'price', Number(e.target.value))}
                size="small"
                inputProps={{ step: 0.01, min: MIN_PACKAGE_PRICE }}
                error={Number(pkg.price) > 0 && Number(pkg.price) < MIN_PACKAGE_PRICE}
                helperText={
                  Number(pkg.price) > 0 && Number(pkg.price) < MIN_PACKAGE_PRICE
                    ? `Minimum $${MIN_PACKAGE_PRICE.toFixed(2)} — Stripe rejects less`
                    : ' '
                }
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
              <TextField label="Badge (optional)" value={pkg.badge} onChange={e => updatePackage(index, 'badge', e.target.value)} size="small" placeholder="e.g. Best Value" />
              <FormControlLabel
                control={<Switch checked={pkg.highlighted} onChange={e => updatePackage(index, 'highlighted', e.target.checked)} size="small" />}
                label={<Typography variant="body2" sx={{ fontSize: 13 }}>Highlight this package</Typography>}
              />
            </Box>

            <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, display: 'block', mb: 1 }}>Features</Typography>
            {pkg.features.map((feat, fi) => (
              <Box key={fi} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <TextField value={feat} onChange={e => updateFeature(index, fi, e.target.value)} size="small" fullWidth placeholder="Feature description" />
                <IconButton size="small" onClick={() => removeFeature(index, fi)} sx={{ color: '#ccc', '&:hover': { color: '#e53935' } }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={() => addFeature(index)} sx={{ textTransform: 'none', fontSize: 12, mt: 0.5 }}>
              Add Feature
            </Button>
          </Box>
        ))}
      </Paper>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ borderRadius: 2 }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
